/** Minimum digit count for a usable phone number. */
export const MIN_PHONE_DIGITS = 7;
/** E.164 maximum digit count. */
export const MAX_PHONE_DIGITS = 15;

export interface PhoneValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validate a phone number's format without inferring a country code.
 *
 * Per the QA decision, we do not auto-detect country codes. Instead we detect
 * clearly invalid formats so the wizard can warn the user and let them fix the
 * CSV. Empty values are treated as valid here (presence is enforced by the
 * required-field rules).
 */
export function validatePhone(value?: string): PhoneValidationResult {
  if (!value || value.trim() === '') {
    return { isValid: true };
  }

  const trimmed = value.trim();

  if (/[a-zA-Z]/.test(trimmed)) {
    return { isValid: false, errorMessage: 'Phone number contains letters.' };
  }

  // Allowed: optional leading +, digits, spaces, and common separators.
  if (!/^\+?[0-9\s\-().]+$/.test(trimmed)) {
    return {
      isValid: false,
      errorMessage: 'Phone number contains invalid characters.',
    };
  }

  const digits = trimmed.replace(/\D/g, '');

  if (digits.length < MIN_PHONE_DIGITS) {
    return {
      isValid: false,
      errorMessage: `Phone number is too short (needs at least ${MIN_PHONE_DIGITS} digits).`,
    };
  }

  if (digits.length > MAX_PHONE_DIGITS) {
    return {
      isValid: false,
      errorMessage: `Phone number is too long (max ${MAX_PHONE_DIGITS} digits).`,
    };
  }

  return { isValid: true };
}

/** Suffix for any "... Primary Phone" column (parent, student, emergency). */
const PHONE_COLUMN_SUFFIX = 'Primary Phone';

export interface InvalidPhoneEntry {
  /** 1-based row number as shown to the user. */
  rowNumber: number;
  field: string;
  value: string;
  message: string;
}

/**
 * Scan parsed CSV rows for invalid phone numbers. Drives the "I understand"
 * acknowledgment before the user continues.
 */
export function collectInvalidPhones(
  rows: Array<Record<string, unknown>>,
): Array<InvalidPhoneEntry> {
  const entries: Array<InvalidPhoneEntry> = [];
  rows.forEach((row, index) => {
    for (const [field, value] of Object.entries(row)) {
      if (!field.endsWith(PHONE_COLUMN_SUFFIX)) continue;
      if (typeof value !== 'string') continue;
      const result = validatePhone(value);
      if (!result.isValid) {
        entries.push({
          rowNumber: index + 1,
          field,
          value,
          message: result.errorMessage ?? 'Invalid phone number.',
        });
      }
    }
  });
  return entries;
}
